import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { notification } from 'antd';
import { FiEdit2, FiImage, FiMinus, FiPlus } from 'react-icons/fi';
import PropertyService from 'service/PropertyService';
import { HOME_PAGE } from 'settings/constant';
import { clearImportEdit, startImportEdit } from 'library/helpers/importEditFlow';
import { OnboardingShell, Spinner as Spin, ob, bp } from 'components/Onboarding';

interface Props {
  propertyId: number | string;
  rail: readonly string[];
  /** Continue was pressed (and any guest-count change saved). */
  onNext: () => void;
}

interface Verified {
  title: string;
  type: string;
  location: string;
  bathrooms: string;
  bedrooms: string;
  guests: number;
  guestsDetailId: number | null;
  amenities: string[];
  description: string;
  photos: string[];
  server: any;
}

const stripHtml = (html: string): string =>
  (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const firstUrl = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return firstUrl(value[0]);
  if (typeof value === 'object') return firstUrl(Object.values(value)[0]);
  return null;
};

/** "We found your property": the host reviews what the import brought over before choosing a plan. */
const VerifyImport: React.FC<Props> = ({ propertyId, rail, onNext }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<Verified | null>(null);
  const [failed, setFailed] = useState(false);
  const [guests, setGuests] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Back on this page: any edit return target has been used up.
  useEffect(() => {
    clearImportEdit();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [propRes, typeRes, fieldRes, photoRes]: any[] = await Promise.all([
          PropertyService.getPropertyDetailsById(propertyId),
          PropertyService.getPropertyTypeList(),
          PropertyService.getPropertyDetailsFieldList(),
          PropertyService.getPhotos(propertyId).catch(() => null),
        ]);
        if (cancelled) return;
        const server = propRes?.result;
        if (propRes?.responseInformation?.responseCode !== 0 || !server) {
          setFailed(true);
          return;
        }

        const fieldName: Record<number, string> = {};
        (fieldRes?.result || []).forEach((f: any) => {
          fieldName[f.id] = String(f.name || '');
        });
        const valueOf = (re: RegExp): { value: number; id: number | null } => {
          const hit = (server.details || []).find((d: any) =>
            re.test(fieldName[d.propertyDetailsId] || d.name || ''),
          );
          return { value: Number(hit?.detailsValue ?? 0), id: hit?.propertyDetailsId ?? null };
        };
        const bedrooms = valueOf(/bedroom/i).value;
        const beds = valueOf(/^beds?$|\bbeds?\b/i).value;
        const bathrooms = valueOf(/bathroom/i).value;
        const guestField = valueOf(/guest/i);

        const type = (typeRes?.result || []).find((t: any) => t.id === server.propertyTypeId);
        const loc = server.location || {};

        const list = photoRes?.result?.imageUrlList || {};
        const all: string[] = Object.values(list).reduce<string[]>(
          (acc, arr) => acc.concat((arr as string[]) || []),
          [],
        );
        const thumb = firstUrl(photoRes?.result?.thumbnailUrl || photoRes?.result?.thumbnail);
        const photos = Array.from(new Set([thumb, ...all].filter((u): u is string => !!u)));

        setData({
          title: server.title || '',
          type:
            /^other$/i.test(type?.name ?? '') && server.propertyTypeOther
              ? server.propertyTypeOther
              : type?.name || '',
          location: [loc.locality || loc.city, loc.state, loc.country || 'India']
            .filter(Boolean)
            .join(', '),
          bathrooms: bathrooms ? plural(bathrooms, 'bathroom') : '',
          bedrooms: bedrooms
            ? `${plural(bedrooms, 'bedroom')}${beds ? ` · ${plural(beds, 'bed')}` : ''}`
            : '',
          guests: guestField.value,
          guestsDetailId: guestField.id,
          amenities: (server.amenities || []).map((a: any) => a.name).filter(Boolean),
          description: stripHtml(server.description || ''),
          photos,
          server,
        });
        setGuests(guestField.value || null);
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const review = useMemo(() => {
    if (!data) return { found: 0, needReview: 0 };
    const checks = [
      data.title,
      data.type,
      data.location,
      data.bathrooms,
      data.bedrooms,
      data.guests,
      data.amenities.length,
      data.description,
      data.photos.length >= 5,
    ];
    return {
      found: (data.server?.details?.length || 0) + data.amenities.length,
      needReview: checks.filter((c) => !c).length,
    };
  }, [data]);

  const goEdit = (): void => {
    localStorage.setItem('newPropertId', String(propertyId));
    startImportEdit(propertyId, `/host/import-listing?id=${propertyId}`);
    navigate(`/host/description?id=${propertyId}`);
  };

  const goOn = async (): Promise<void> => {
    if (!data) return;
    setSaving(true);
    try {
      // Only the guest count can be changed on this screen; save it if it was.
      if (guests !== null && guests !== data.guests && data.guestsDetailId != null) {
        const details = (data.server.details || []).map((d: any) =>
          d.propertyDetailsId === data.guestsDetailId ? { ...d, detailsValue: guests } : d,
        );
        await PropertyService.updateProperty({ ...data.server, propertyId, details });
      }
      localStorage.setItem('newPropertId', String(propertyId));
      onNext();
    } catch (_) {
      notification.error({
        message: 'Couldn’t save your changes',
        description: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const hero = data?.photos[0];
  const thumbs = data?.photos.slice(1, 4) || [];

  return (
    <OnboardingShell
      section={1}
      railItems={rail}
      wide
      title="We found your property"
      intro="We’ve brought over the details we could find. Review them before we create your listing."
      headerBack={() => navigate(HOME_PAGE)}
      onBack={() => navigate(HOME_PAGE)}
      onContinue={goOn}
      continueLoading={saving}
      continueDisabled={!data}
      showImport={false}
    >
      {failed ? (
        <Note>
          We couldn’t load your imported listing right now. You can still continue and review it in
          the next steps.
        </Note>
      ) : !data ? (
        <Center>
          <Spin />
        </Center>
      ) : (
        <>
          <Counts>
            <span className="ok">{review.found} details found</span>
            {review.needReview > 0 ? (
              <>
                {' · '}
                <span className="warn">{review.needReview} need your review</span>
              </>
            ) : null}
          </Counts>

          <Card>
            <Media>
              <Hero>
                {hero ? (
                  <img src={hero} alt={data.title} onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                ) : (
                  <span className="ph">
                    <FiImage size={32} />
                  </span>
                )}
              </Hero>
              {thumbs.length ? (
                <Thumbs>
                  {thumbs.map((url) => (
                    <img
                      key={url}
                      src={url}
                      alt=""
                      onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
                    />
                  ))}
                </Thumbs>
              ) : null}
              <Count>
                <FiImage size={16} /> {plural(data.photos.length, 'photo')} imported
              </Count>
            </Media>

            <Details>
              <Head>
                <h2>Property details</h2>
                <EditBtn type="button" onClick={goEdit}>
                  <FiEdit2 size={16} /> Edit
                </EditBtn>
              </Head>

              <Grid>
                <Tile label="Property name" value={data.title} />
                <Tile label="Property type" value={data.type} />
                <Tile label="Location" value={data.location} />
                <Tile label="Bathrooms" value={data.bathrooms} />
                <TileBox>
                  <span className="k">Guests</span>
                  {guests !== null ? (
                    <Stepper>
                      <button
                        type="button"
                        aria-label="Fewer guests"
                        onClick={() => setGuests(Math.max(1, guests - 1))}
                      >
                        <FiMinus size={16} />
                      </button>
                      <b>{plural(guests, 'guest')}</b>
                      <button
                        type="button"
                        aria-label="More guests"
                        onClick={() => setGuests(guests + 1)}
                      >
                        <FiPlus size={16} />
                      </button>
                    </Stepper>
                  ) : (
                    <span className="v missing">Needs review</span>
                  )}
                </TileBox>
                <Tile label="Bedrooms" value={data.bedrooms} />
              </Grid>

              <TileBox $block>
                <span className="k">Amenities</span>
                {data.amenities.length ? (
                  <Pills>
                    {data.amenities.slice(0, 12).map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                    {data.amenities.length > 12 ? <li>+{data.amenities.length - 12} more</li> : null}
                  </Pills>
                ) : (
                  <span className="v missing">Needs review</span>
                )}
              </TileBox>

              <TileBox $block>
                <span className="k">Description</span>
                {data.description ? (
                  <p className="desc">{data.description}</p>
                ) : (
                  <span className="v missing">Needs review</span>
                )}
              </TileBox>
            </Details>
          </Card>
        </>
      )}
    </OnboardingShell>
  );
};

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <TileBox>
    <span className="k">{label}</span>
    <span className={value ? 'v' : 'v missing'}>{value || 'Needs review'}</span>
  </TileBox>
);

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const Note = styled.p`
  margin: 0;
  padding: 16px 18px;
  border: 1px solid rgba(180, 83, 9, 0.25);
  border-radius: 14px;
  background: ${ob.warningSoft};
  color: ${ob.warning};
  font-size: 14.5px;
`;

const Counts = styled.p`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  .ok {
    color: ${ob.success};
  }
  .warn {
    color: ${ob.warning};
  }
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border: 1px solid ${ob.border};
  border-radius: 28px;
  background: #fff;
  box-shadow: ${ob.shadowCard};
  overflow: hidden;
  ${bp.lg} {
    grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  }
`;

const Media = styled.div`
  padding: 20px;
  ${bp.lg} {
    padding: 36px;
    border-right: 1px solid ${ob.border};
  }
`;

const Hero = styled.div`
  aspect-ratio: 4 / 3;
  border-radius: 22px;
  overflow: hidden;
  background: ${ob.mutedBg};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: ${ob.muted};
  }
`;

const Thumbs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 14px;
    object-fit: cover;
    background: ${ob.mutedBg};
  }
`;

const Count = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 0 0;
  font-size: 14.5px;
  color: ${ob.muted};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  ${bp.lg} {
    padding: 36px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  h2 {
    margin: 0;
    font-family: ${ob.fontDisplay};
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
`;

const EditBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 20px;
  border: 1px solid ${ob.borderStrong};
  border-radius: 14px;
  background: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${ob.shadowCard};
  &:hover {
    border-color: ${ob.primary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  ${bp.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TileBox = styled.div<{ $block?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  border: 1px solid ${ob.border};
  border-radius: 20px;
  background: ${ob.surface};
  .k {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${ob.muted};
  }
  .v {
    font-size: 17px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .missing {
    color: ${ob.warning};
    font-weight: 500;
  }
  .desc {
    margin: 0;
    font-size: 15.5px;
    line-height: 1.6;
    color: ${ob.muted};
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-line;
  }
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  b {
    font-size: 17px;
  }
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid ${ob.borderStrong};
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    box-shadow: ${ob.shadowCard};
    &:hover {
      border-color: ${ob.primary};
    }
  }
`;

const Pills = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    padding: 8px 16px;
    border-radius: 999px;
    background: ${ob.mutedBg};
    font-size: 15px;
  }
`;

export default VerifyImport;
